import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useDispatch, useSelector } from "react-redux";
import Navbar from "../../navbar";
import Header from "../../header";
import Pagination from "../dashboard/appPagination";
import ConfirmationModal from "../../common/confirmationModal.jsx";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import iconSearch from "../../../assets/media/icon-search.svg";
import {
  getLockedUsers,
  unlockUser,
} from "../../../store/reducers/userSlice.js";

const columnHelper = createColumnHelper();

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const LockedAccounts = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const authToken = user?.access_token;
  const lockedUsers = useSelector((state) => state.userdata.lockedUsers) || [];
  const lockedLoading = useSelector((state) => state.userdata.lockedLoading);
  const unlockLoading = useSelector((state) => state.userdata.unlockLoading);

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchLockedUsers = useCallback(() => {
    if (!authToken) return;
    dispatch(getLockedUsers({ authToken }));
  }, [authToken, dispatch]);

  useEffect(() => {
    fetchLockedUsers();
  }, [fetchLockedUsers]);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return lockedUsers;
    return lockedUsers.filter((item) => {
      const companyName = item?.company?.company_name || "";
      return (
        String(item?.id || "").toLowerCase().includes(query) ||
        item?.full_name?.toLowerCase()?.includes(query) ||
        item?.email?.toLowerCase()?.includes(query) ||
        item?.phone?.toLowerCase()?.includes(query) ||
        item?.user_role?.toLowerCase()?.includes(query) ||
        companyName.toLowerCase().includes(query)
      );
    });
  }, [lockedUsers, searchQuery]);

  const getTotalPages = () => {
    const validItemsPerPage =
      typeof itemsPerPage === "number" && itemsPerPage > 0 ? itemsPerPage : 10;
    return Math.max(1, Math.ceil(filteredUsers.length / validItemsPerPage));
  };

  const paginatedUsers = useMemo(() => {
    const validItemsPerPage =
      typeof itemsPerPage === "number" && itemsPerPage > 0 ? itemsPerPage : 10;
    const start = (currentPage - 1) * validItemsPerPage;
    return filteredUsers.slice(start, start + validItemsPerPage);
  }, [filteredUsers, currentPage, itemsPerPage]);

  useEffect(() => {
    const totalPages = getTotalPages();
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filteredUsers.length, itemsPerPage, currentPage]);

  const handleSearchInputChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const openUnlockModal = (row) => {
    setSelectedUser(row);
    setShowUnlockModal(true);
  };

  const handleUnlockConfirm = async () => {
    if (!selectedUser?.id || !authToken) return;
    try {
      await dispatch(
        unlockUser({ userId: selectedUser.id, authToken })
      ).unwrap();
      setShowUnlockModal(false);
      setSelectedUser(null);
    } catch (_) {
      // Error toast is handled by the API interceptor / thunk
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) =>
          (currentPage - 1) * itemsPerPage + info.row.index + 1,
      }),
      columnHelper.accessor("id", {
        header: "User Id",
        cell: (info) => `GB-${info.getValue()}`,
      }),
      columnHelper.display({
        id: "name",
        header: "Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <strong style={{ whiteSpace: "nowrap" }}>
              {row.full_name || "—"}
            </strong>
          );
        },
      }),
      columnHelper.accessor("email", {
        header: "Email",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }}>{info.getValue() || "—"}</span>
        ),
      }),
      columnHelper.accessor("phone", {
        header: "Phone No.",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }}>{info.getValue() || "—"}</span>
        ),
      }),
      columnHelper.display({
        id: "company",
        header: "Company",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {info.row.original?.company?.company_name || "—"}
          </span>
        ),
      }),
      columnHelper.accessor("user_role", {
        header: "User role",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }}>{info.getValue() || "—"}</span>
        ),
      }),
      columnHelper.accessor("failed_login_attempts", {
        header: "Failed Attempts",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor("locked_until", {
        header: "Locked Until",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {formatDateTime(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor("last_failed_login", {
        header: "Last Failed Login",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {formatDateTime(info.getValue())}
          </span>
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id={`locked-actions-${row.id}`}
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby={`locked-actions-${row.id}`}
                >
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item"
                      onClick={(e) => {
                        e.preventDefault();
                        openUnlockModal(row);
                      }}
                    >
                      Unlock
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          );
        },
      }),
    ],
    [currentPage, itemsPerPage]
  );

  const table = useReactTable({
    data: paginatedUsers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <Navbar />
      <Header handleSearchInputChange={handleSearchInputChange} />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          <div className="boardHeader">
            <div className="boardHeadLeft">
              <h2>Locked Accounts</h2>
            </div>
            <div className="boardHeadRight">
              <div className="searchForm">
                <input
                  type="text"
                  placeholder="Search locked accounts"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  className="form-control"
                />
                <button type="button" className="btnSearch">
                  <img src={iconSearch} alt="Search" />
                </button>
              </div>
            </div>
          </div>

          <div className="boardContent">
            <div className="table-responsive">
              <table className="table">
                <thead className="boardTheadcompany">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th key={header.id} style={{ whiteSpace: "nowrap" }}>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {lockedLoading ? (
                    <tr>
                      <td
                        className="text-center"
                        colSpan={table.getHeaderGroups()?.[0]?.headers.length || 11}
                      >
                        <div className="d-flex justify-content-center my-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : table.getRowModel().rows.length > 0 ? (
                    table.getRowModel().rows.map((row) => (
                      <tr key={row.id} className="boardTrowcompany">
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} style={{ whiteSpace: "nowrap" }}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={table.getHeaderGroups()?.[0]?.headers.length || 11}
                        className="text-center"
                      >
                        <h2 className="text-danger my-4 py-4">
                          No Locked Accounts Found
                        </h2>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {!lockedLoading && filteredUsers.length > 0 && (
            <Pagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={getTotalPages}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              onItemsPerPageChange={(value) => {
                setItemsPerPage(value);
                setCurrentPage(1);
              }}
              totalItems={filteredUsers.length}
            />
          )}
        </div>
      </div>

      <ConfirmationModal
        show={showUnlockModal}
        handleClose={() => {
          if (unlockLoading) return;
          setShowUnlockModal(false);
          setSelectedUser(null);
        }}
        title="Unlock Account"
        description={
          selectedUser
            ? `Unlock ${selectedUser.full_name || selectedUser.email} (GB-${selectedUser.id})? This clears failed login attempts and restores access immediately.`
            : "Unlock this account?"
        }
        onConfirm={handleUnlockConfirm}
      />
    </div>
  );
};

export default LockedAccounts;
